/*
 * Paint the glyphs out of a patch of the rendered page, keeping everything behind them.
 *
 * The anonymized export deletes a finding's glyphs from the page content, so a watermark, a logo
 * or a shaded table cell behind them stays whole. A flat box in the paper's colour would cut a hole
 * into all of those, so the preview takes the page's own pixels under the cover and replaces only
 * the ones the text painted, in four steps:
 *
 * 1. The line's background is the most frequent colour of its text body. Antialiased text spreads
 *    over many shades while the paper, the cell fill or the watermark is one colour, so even dense
 *    lowercase, more ink than paper by area, leaves it the commonest colour (a median lands on a
 *    grey).
 * 2. The line's band: a cover reaches from above the font's ascent to below its descent, which in
 *    a tight table row is further than the row. Going up and down from the text body, the first row
 *    in one colour across the whole patch and clearly unlike the background — a table rule, the
 *    next cell's fill — ends the band, and everything beyond it is left exactly as it is. A
 *    watermark's soft, curved edge is never one colour across the patch, so it does not end it.
 * 3. Text pixels: each column's background runs linearly from the band's top to its bottom edge,
 *    so a watermark shading across the line counts as background. A pixel is text where it has
 *    moved from there towards the ink by `TEXT_COVERAGE` or more, and its fainter antialiased
 *    fringe where it touches text. Direction matters as much as distance: text mixed into its
 *    background moves a pixel straight towards the ink, while the stronger blue of a watermark, a
 *    cell fill or a coloured rule lies off to the side of that line and is never text.
 * 4. Every text pixel is rebuilt from the nearest pixels above and below it in its column that are
 *    not text; a column that is text across the whole band borrows from its left and right
 *    neighbours.
 */

export type Rgb = [number, number, number];

/** Share of the way from background to ink from which a pixel is text. */
const TEXT_COVERAGE = 0.12;

/** Share from which a pixel next to text is taken for its antialiased fringe. */
const FRINGE_COVERAGE = 0.03;

/**
 * How far off the background-to-ink line (RGB distance, relative to how far along it) a pixel may
 * lie and still be text. Black text over a light-blue watermark stays within a few percent; the
 * watermark itself, a cell fill or a coloured rule lies at around a third.
 */
const OFF_LINE_SHARE = 0.2;
/** And in absolute RGB units, for the palest pixels where the share alone is too strict. */
const OFF_LINE_SLACK = 6;

/** Ink and background closer than this (squared RGB distance) leave nothing to tell text by. */
const MIN_CONTRAST_SQUARED = 32 * 32;

/** Where the text body lies in a cover, as shares of its height from the top (ascent to descent). */
const BODY_TOP = 0.5;
const BODY_BOTTOM = 0.8;

/**
 * A row this far (RGB distance) from the line's background, in one colour across the patch, ends
 * the line's band: a table rule or the next cell's fill (a striped row is ~45 from white), not the
 * shading of a watermark (a few units per pixel row).
 */
const RULE_DISTANCE = 30;

/** Share of a row within `UNIFORM_DISTANCE` of its median for the row to count as one colour. */
const UNIFORM_SHARE = 0.9;
const UNIFORM_DISTANCE = 10;

const distance = (a: ArrayLike<number>, b: ArrayLike<number>): number =>
  Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

/**
 * The most frequent colour of the RGBA pixels from pixel index `from` (inclusive) to `to`
 * (exclusive), grouped by the top five bits per channel and averaged within the group.
 */
const modeColour = (data: Uint8ClampedArray, from: number, to: number): Rgb => {
  const groups = new Map<number, [number, number, number, number]>();
  for (let index = from; index < to; index += 1) {
    const offset = index * 4;
    const key = ((data[offset] >> 3) << 10) | ((data[offset + 1] >> 3) << 5) | (data[offset + 2] >> 3);
    const group = groups.get(key) ?? [0, 0, 0, 0];
    group[0] += 1;
    group[1] += data[offset];
    group[2] += data[offset + 1];
    group[3] += data[offset + 2];
    groups.set(key, group);
  }
  let best: [number, number, number, number] | undefined;
  for (const group of groups.values()) if (!best || group[0] > best[0]) best = group;
  return best ? [best[1] / best[0], best[2] / best[0], best[3] / best[0]] : [255, 255, 255];
};

/** Per-channel median of the RGBA pixels from pixel index `from` (inclusive) to `to` (exclusive). */
const medianColour = (data: Uint8ClampedArray, from: number, to: number): Rgb => {
  const count = to - from;
  const channel = (offset: number) => {
    const values = new Uint8Array(count);
    for (let index = 0; index < count; index += 1) values[index] = data[(from + index) * 4 + offset];
    values.sort();
    return values[count >> 1];
  };
  return [channel(0), channel(1), channel(2)];
};

/** Parse a `#rrggbb` colour. */
export const parseHex = (hex: string): Rgb => {
  const value = Number.parseInt(hex.slice(1, 7), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
};

/**
 * Return a copy of the RGBA pixels `data` (`width` × `height`) with the glyphs painted in `ink`
 * removed. `paper` is the page's colour around the patch; it stands in where a text pixel has no
 * clean neighbour at all. Alpha is left as it is.
 */
export const eraseGlyphs = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  paper: Rgb,
  ink: Rgb
): Uint8ClampedArray<ArrayBuffer> => {
  const out = new Uint8ClampedArray(data);
  const count = width * height;
  if (count === 0) return out;

  // The text body's background, and the band of rows the line occupies.
  const bodyTop = Math.min(Math.floor(height * BODY_TOP), height - 1);
  const bodyBottom = Math.max(Math.min(Math.ceil(height * BODY_BOTTOM), height), bodyTop + 1);
  const behind = modeColour(data, bodyTop * width, bodyBottom * width);
  const isRule = (y: number): boolean => {
    const median = medianColour(data, y * width, (y + 1) * width);
    if (distance(median, behind) <= RULE_DISTANCE) return false;
    let uniform = 0;
    for (let x = 0; x < width; x += 1) {
      if (distance(data.subarray((y * width + x) * 4, (y * width + x) * 4 + 3), median) <= UNIFORM_DISTANCE) {
        uniform += 1;
      }
    }
    return uniform >= width * UNIFORM_SHARE;
  };
  let first = bodyTop;
  while (first > 0 && !isRule(first - 1)) first -= 1;
  let last = bodyBottom - 1;
  while (last < height - 1 && !isRule(last + 1)) last += 1;

  if (distance(ink, behind) ** 2 < MIN_CONTRAST_SQUARED) return out;

  // How far a pixel has moved from `from` towards the ink; 0 when it lies off that line, as a
  // watermark, a fill or a coloured rule does.
  const coverageFrom = (pixel: ArrayLike<number>, from: ArrayLike<number>): number => {
    const w0 = ink[0] - from[0];
    const w1 = ink[1] - from[1];
    const w2 = ink[2] - from[2];
    const squared = w0 * w0 + w1 * w1 + w2 * w2;
    if (squared < MIN_CONTRAST_SQUARED) return 0;
    const v0 = pixel[0] - from[0];
    const v1 = pixel[1] - from[1];
    const v2 = pixel[2] - from[2];
    const along = (v0 * w0 + v1 * w1 + v2 * w2) / squared;
    const off = Math.hypot(v0 - along * w0, v1 - along * w1, v2 - along * w2);
    return off <= OFF_LINE_SHARE * Math.abs(along) * Math.sqrt(squared) + OFF_LINE_SLACK ? along : 0;
  };
  const pixelAt = (x: number, y: number) => data.subarray((y * width + x) * 4, (y * width + x) * 4 + 3);
  const clean = (pixel: ArrayLike<number>) => coverageFrom(pixel, behind) < TEXT_COVERAGE;

  const coverage = new Float32Array(count);
  const text = new Uint8Array(count);
  for (let x = 0; x < width; x += 1) {
    // This column's background at the band's top and bottom edge, run linearly in between, so a
    // watermark's shading across the line is its own background; an edge pixel that is itself text
    // (an accent, a descender) gives way to the other edge, or to the line's background.
    const topPixel = pixelAt(x, first);
    const bottomPixel = pixelAt(x, last);
    const top: ArrayLike<number> = clean(topPixel) ? topPixel : clean(bottomPixel) ? bottomPixel : behind;
    const bottom: ArrayLike<number> = clean(bottomPixel) ? bottomPixel : top;
    for (let y = first; y <= last; y += 1) {
      const share = last > first ? (y - first) / (last - first) : 0;
      const local = [
        top[0] + (bottom[0] - top[0]) * share,
        top[1] + (bottom[1] - top[1]) * share,
        top[2] + (bottom[2] - top[2]) * share,
      ];
      const index = y * width + x;
      coverage[index] = coverageFrom(pixelAt(x, y), local);
      text[index] = coverage[index] >= TEXT_COVERAGE ? 1 : 0;
    }
  }
  // The fringe: faint pixels touching text. Taken from the first mask only, so it cannot creep.
  const fringe: number[] = [];
  for (let y = first; y <= last; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      if (text[index] || coverage[index] < FRINGE_COVERAGE) continue;
      if (
        (x > 0 && text[index - 1]) ||
        (x < width - 1 && text[index + 1]) ||
        (y > first && text[index - width]) ||
        (y < last && text[index + width])
      ) {
        fringe.push(index);
      }
    }
  }
  for (const index of fringe) text[index] = 1;

  const columnFull = new Uint8Array(width);
  for (let x = 0; x < width; x += 1) {
    let y = first;
    while (y <= last) {
      if (!text[y * width + x]) {
        y += 1;
        continue;
      }
      // A run of text pixels in this column, from `start` to `end - 1`.
      const start = y;
      while (y <= last && text[y * width + x]) y += 1;
      const end = y;
      if (start === first && end === last + 1) {
        columnFull[x] = 1;
        continue;
      }
      const above = start > first ? (start - 1) * width + x : -1;
      const below = end <= last ? end * width + x : -1;
      for (let row = start; row < end; row += 1) {
        const offset = (row * width + x) * 4;
        // Linear between the two clean neighbours; the one there is when the run touches the band's edge.
        const share = (row - start + 1) / (end - start + 1);
        for (let channel = 0; channel < 3; channel += 1) {
          const upper = above >= 0 ? data[above * 4 + channel] : data[below * 4 + channel];
          const lower = below >= 0 ? data[below * 4 + channel] : upper;
          out[offset + channel] = upper + (lower - upper) * share;
        }
      }
    }
  }

  // Columns that are text across the whole band borrow from the nearest clean column on either side.
  for (let x = 0; x < width; x += 1) {
    if (!columnFull[x]) continue;
    let left = x - 1;
    while (left >= 0 && columnFull[left]) left -= 1;
    let right = x + 1;
    while (right < width && columnFull[right]) right += 1;
    for (let y = first; y <= last; y += 1) {
      const offset = (y * width + x) * 4;
      for (let channel = 0; channel < 3; channel += 1) {
        const fromLeft = left >= 0 ? out[(y * width + left) * 4 + channel] : undefined;
        const fromRight = right < width ? out[(y * width + right) * 4 + channel] : undefined;
        out[offset + channel] =
          fromLeft !== undefined && fromRight !== undefined
            ? fromLeft + ((fromRight - fromLeft) * (x - left)) / (right - left)
            : (fromLeft ?? fromRight ?? paper[channel]);
      }
    }
  }
  return out;
};
